/**
 * Compact DSL parser for draw.io diagrams.
 *
 * Syntax:
 *   !!diag name!!                    — start diagram definition
 *     rect r1: "Label" {fill=#DAE8FC, round}
 *     ellipse e1 {fill=#FFF2CC}
 *     r1 -> e1 {arrow=classic, label="go"}
 *   !!diagram=name!!                 — render diagram as SVG
 *
 * Node:  <shape> <id>: "label" {style-props}
 * Edge:  <from> -> <to> {edge-props}
 */

import { escapeHtml } from "./escapeHtml.js";

// ── DSL Types ────────────────────────────────────────────────────────────────

export type DiagramNodeShape =
  | "rect" | "ellipse" | "rhombus" | "triangle" | "hexagon"
  | "cloud" | "cylinder" | "diamond" | "note" | "document"
  | "swimlane" | "actor" | "text";

export interface DiagramNode {
  id: string;
  shape: DiagramNodeShape;
  label: string;
  styles: Record<string, string>;
}

export type ArrowType = "classic" | "open" | "block" | "diamond" | "none";

export interface DiagramEdge {
  from: string;
  to: string;
  styles: Record<string, string>;
  label: string;
}

export interface DiagramData {
  name: string;
  nodes: DiagramNode[];
  edges: DiagramEdge[];
}

export type MaxDiagrams = Record<string, DiagramData>;

// ── Shape → draw.io shape name mapping ───────────────────────────────────────

const SHAPE_MAP: Record<DiagramNodeShape, string> = {
  rect: "rounded=1;whiteSpace=wrap;html=1;",
  ellipse: "ellipse;whiteSpace=wrap;html=1;perimeter=ellipsePerimeter;",
  rhombus: "rhombus;whiteSpace=wrap;html=1;perimeter=rhombusPerimeter;",
  triangle: "triangle;whiteSpace=wrap;html=1;perimeter=trianglePerimeter;",
  hexagon: "hexagon;whiteSpace=wrap;html=1;perimeter=hexagonPerimeter2;",
  cloud: "shape=cloud;whiteSpace=wrap;html=1;",
  cylinder: "shape=cylinder;whiteSpace=wrap;html=1;",
  diamond: "rhombus;whiteSpace=wrap;html=1;perimeter=rhombusPerimeter;",
  note: "shape=note;whiteSpace=wrap;html=1;",
  document: "shape=document;whiteSpace=wrap;html=1;",
  swimlane: "swimlane;startSize=25;html=1;whiteSpace=wrap;",
  actor: "shape=actor;whiteSpace=wrap;html=1;",
  text: "text;html=1;align=left;verticalAlign=top;spacingTop=2;",
};

const VALID_SHAPES = new Set<string>(Object.keys(SHAPE_MAP));

// ── Style prop → draw.io style string mapping ────────────────────────────────

const ARROW_STYLES: Record<ArrowType, { arrow: string; fill: string }> = {
  classic: { arrow: "endArrow=classic", fill: "endFill=1" },
  open: { arrow: "endArrow=open", fill: "endFill=0" },
  block: { arrow: "endArrow=block", fill: "endFill=1" },
  diamond: { arrow: "endArrow=diamond", fill: "endFill=0" },
  none: { arrow: "endArrow=none", fill: "" },
};

// ── Parser ───────────────────────────────────────────────────────────────────

/** Check if a body inside !!...!! is a diagram definition opener: `diag <name>`. */
export function isDiagramDefBody(body: string): string | null {
  const trimmed = body.trim();
  const m = /^diag\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*$/.exec(trimmed);
  return m ? m[1]! : null;
}

/** Check if a body is a diagram reference: `diagram=<name>` or `diagram-svg=<name>`. */
export function isDiagramRefBody(body: string): { name: string; inline: boolean } | null {
  const trimmed = body.trim();
  const mInline = /^diagram(?:-svg)?=([a-zA-Z_][a-zA-Z0-9_]*)$/.exec(trimmed);
  if (mInline) return { name: mInline[1]!, inline: true };
  return null;
}

/** Parse a diagram definition body into a DiagramData. */
export function parseDiagramBody(name: string, body: string): DiagramData {
  const nodes: DiagramNode[] = [];
  const edges: DiagramEdge[] = [];
  const lines = body.split("\n").map((l) => l.trim()).filter((l) => l.length > 0 && !l.startsWith("//"));

  for (const line of lines) {
    const edgeMatch = parseEdgeLine(line);
    if (edgeMatch) {
      edges.push(edgeMatch);
      continue;
    }
    const nodeMatch = parseNodeLine(line);
    if (nodeMatch) {
      nodes.push(nodeMatch);
    }
  }

  return { name, nodes, edges };
}

function parseNodeLine(line: string): DiagramNode | null {
  const s = line.trim();
  if (!s) return null;

  const shapeRe = /^(\w+)\s+([\w-]+)\s*/;
  const shapeM = shapeRe.exec(s);
  if (!shapeM) return null;

  const shape = shapeM[1]! as DiagramNodeShape;
  if (!VALID_SHAPES.has(shape)) return null;

  const id = shapeM[2]!;
  let rest = s.slice(shapeM[0].length);

  let label = "";
  const labelM = /^\s*:\s*"((?:[^"\\]|\\.)*)"\s*/.exec(rest);
  if (labelM) {
    label = labelM[1]!.replace(/\\"/g, '"').replace(/\\\\/g, "\\");
    rest = rest.slice(labelM[0].length);
  } else {
    label = id;
  }

  const styles: Record<string, string> = {};
  const propM = /^\{\s*([\s\S]*?)\s*\}$/.exec(rest.trim());
  if (propM) {
    parseStyleProps(propM[1]!, styles);
  }

  return { id, shape, label, styles };
}

function parseEdgeLine(line: string): DiagramEdge | null {
  const s = line.trim();
  if (!s) return null;

  const edgeRe = /^([\w-]+)\s*->\s*([\w-]+)\s*/;
  const m = edgeRe.exec(s);
  if (!m) return null;

  const from = m[1]!;
  const to = m[2]!;
  let rest = s.slice(m[0].length);

  const styles: Record<string, string> = {};
  let label = "";

  const propM = /^\{\s*([\s\S]*?)\s*\}$/.exec(rest.trim());
  if (propM) {
    const props = propM[1]!;
    const labelM = /label\s*=\s*"((?:[^"\\]|\\.)*)"/.exec(props);
    if (labelM) {
      label = labelM[1]!.replace(/\\"/g, '"').replace(/\\\\/g, "\\");
    }
    parseStyleProps(props, styles);
  }

  return { from, to, styles, label };
}

function parseStyleProps(propStr: string, out: Record<string, string>): void {
  const pairs = propStr.split(/,\s*/).map((s) => s.trim()).filter(Boolean);
  for (const pair of pairs) {
    const eqIdx = pair.indexOf("=");
    if (eqIdx > 0) {
      const key = pair.slice(0, eqIdx).trim();
      const val = pair.slice(eqIdx + 1).trim();
      out[key] = val;
    } else if (pair === "round" || pair === "rounded") {
      out["round"] = "1";
    } else if (pair === "dashed") {
      out["dashed"] = "1";
    } else if (pair === "shadow") {
      out["shadow"] = "1";
    } else if (pair === "glass") {
      out["glass"] = "1";
    } else if (pair === "curved") {
      out["curved"] = "1";
    } else if (pair === "fixed") {
      out["aspect"] = "fixed";
    }
  }
}

// ── draw.io XML generation ──────────────────────────────────────────────────

export interface DrawioLayoutOpts {
  pageWidth?: number;
  pageHeight?: number;
  scale?: number;
}

/** Generate draw.io mxGraphModel XML from a DiagramData. */
export function buildDrawioXml(data: DiagramData, opts: DrawioLayoutOpts = {}): string {
  const { pageWidth = 800, pageHeight = 600 } = opts;
  const nodeMap = new Map<string, DiagramNode>(data.nodes.map((n) => [n.id, n]));
  const adj = new Map<string, Set<string>>();
  const allNodeIds: string[] = [];
  for (const n of data.nodes) {
    adj.set(n.id, new Set());
    allNodeIds.push(n.id);
  }
  for (const e of data.edges) {
    if (nodeMap.has(e.from)) adj.get(e.from)!.add(e.to);
  }

  const color = new Map<string, number>();
  for (const id of allNodeIds) color.set(id, 0);
  const backSet = new Set<string>();
  function dfs(id: string) {
    color.set(id, 1);
    for (const next of adj.get(id) ?? []) {
      if (color.get(next) === 1) backSet.add(`${id}->${next}`);
      else if (color.get(next) === 0) dfs(next);
    }
    color.set(id, 2);
  }
  for (const id of allNodeIds) if (color.get(id) === 0) dfs(id);

  const rank = new Map<string, number>();
  for (const id of allNodeIds) rank.set(id, 0);
  for (let pass = 0; pass < allNodeIds.length; pass++) {
    for (const e of data.edges) {
      if (backSet.has(`${e.from}->${e.to}`)) continue;
      const proposed = (rank.get(e.from) ?? 0) + 1;
      if (proposed > (rank.get(e.to) ?? 0)) rank.set(e.to, proposed);
    }
  }

  const rankGroups = new Map<number, string[]>();
  for (const id of allNodeIds) {
    const r = rank.get(id)!;
    if (!rankGroups.has(r)) rankGroups.set(r, []);
    rankGroups.get(r)!.push(id);
  }

  const maxRank = Math.max(...Array.from(rank.values()), 0);
  const maxRowLen = Math.max(...Array.from(rankGroups.values()).map((g) => g.length), 1);
  const NODE_W = 120;
  const NODE_H = 50;
  const NODE_GAP_X = 50;
  const RANK_GAP_Y = 100;
  const PAD_X = 50;
  const PAD_Y = 40;

  const totalW = Math.max(pageWidth, PAD_X * 2 + maxRowLen * (NODE_W + NODE_GAP_X) - NODE_GAP_X);
  const totalH = Math.max(pageHeight, PAD_Y * 2 + (maxRank + 1) * (NODE_H + RANK_GAP_Y) - RANK_GAP_Y);

  const positions = new Map<string, { x: number; y: number }>();
  for (const [r, ids] of rankGroups) {
    const rowW = ids.length * NODE_W + (ids.length - 1) * NODE_GAP_X;
    const startX = (totalW - rowW) / 2;
    for (let i = 0; i < ids.length; i++) {
      positions.set(ids[i]!, {
        x: startX + i * (NODE_W + NODE_GAP_X),
        y: PAD_Y + r! * (NODE_H + RANK_GAP_Y),
      });
    }
  }

  const parts: string[] = [
    '<mxGraphModel dx="0" dy="0" grid="1" gridSize="10" guides="1"',
    '  tooltips="1" connect="1" arrows="1" fold="1"',
    `  page="1" pageScale="1" pageWidth="${totalW}" pageHeight="${totalH}" math="0" shadow="0">`,
    "  <root>",
    '    <mxCell id="0" />',
    '    <mxCell id="1" parent="0" />',
  ];

  for (const n of data.nodes) {
    const pos = positions.get(n.id)!;
    const style = buildNodeStyle(n);
    const escLabel = xmlEscape(n.label);
    parts.push(
      `    <mxCell id="${xmlAttr(n.id)}" value="${escLabel}" style="${xmlAttr(style)}" vertex="1" parent="1">`,
    );
    parts.push(
      `      <mxGeometry x="${pos.x}" y="${pos.y}" width="${NODE_W}" height="${NODE_H}" as="geometry" />`,
    );
    parts.push(`    </mxCell>`);
  }

  let edgeIdx = 0;
  for (const e of data.edges) {
    if (!nodeMap.has(e.from) || !nodeMap.has(e.to)) continue;
    const style = buildEdgeStyle(e);
    const escLabel = xmlEscape(e.label);
    edgeIdx++;
    parts.push(
      `    <mxCell id="edge-${edgeIdx}" value="${escLabel}" style="${xmlAttr(style)}" edge="1" parent="1" source="${xmlAttr(e.from)}" target="${xmlAttr(e.to)}">`,
    );
    parts.push(`      <mxGeometry relative="1" as="geometry" />`);
    parts.push(`    </mxCell>`);
  }

  parts.push("  </root>");
  parts.push("</mxGraphModel>");
  return parts.join("\n");
}

function buildNodeStyle(node: DiagramNode): string {
  const base = SHAPE_MAP[node.shape] ?? "";
  const parts: string[] = [];

  for (const [key, val] of Object.entries(node.styles)) {
    if (key === "round") {
      parts.push("rounded=1");
      if (!base.includes("whiteSpace=wrap")) parts.push("whiteSpace=wrap");
    } else if (key === "dashed") {
      parts.push("dashed=1");
    } else if (key === "shadow") {
      parts.push("shadow=1");
    } else if (key === "glass") {
      parts.push("glass=1");
    } else if (key === "aspect") {
      parts.push(`aspect=${val}`);
    } else if (key === "direction") {
      parts.push(`direction=${val}`);
    } else if (key === "fill") {
      parts.push(`fillColor=${val}`);
    } else if (key === "stroke") {
      parts.push(`strokeColor=${val}`);
    } else if (key === "fontColor") {
      parts.push(`fontColor=${val}`);
    } else if (key === "fontSize") {
      parts.push(`fontSize=${val}`);
    } else if (key === "opacity") {
      parts.push(`opacity=${val}`);
    } else if (key === "strokeWidth") {
      parts.push(`strokeWidth=${val}`);
    }
  }

  if (!parts.length && !base.includes("html=")) parts.push("html=1");
  return (base + ";" + parts.join(";")).replace(/;+/g, ";").replace(/^;|;$/g, "");
}

function buildEdgeStyle(edge: DiagramEdge): string {
  const parts: string[] = ["html=1"];

  for (const [key, val] of Object.entries(edge.styles)) {
    if (key === "dashed") parts.push("dashed=1");
    else if (key === "curved") parts.push("curved=1");
    else if (key === "color") parts.push(`strokeColor=${val}`);
    else if (key === "strokeWidth") parts.push(`strokeWidth=${val}`);
    else if (key === "arrow" && ARROW_STYLES[val as ArrowType]) {
      const a = ARROW_STYLES[val as ArrowType]!;
      parts.push(a.arrow);
      if (a.fill) parts.push(a.fill);
    }
  }

  if (!parts.some((p) => p.startsWith("endArrow="))) parts.push("endArrow=classic");
  return parts.join(";");
}

function xmlEscape(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function xmlAttr(s: string): string {
  return s.replace(/"/g, "&quot;");
}

// ── SVG rendering (self-contained, no external library) ─────────────────────

const SVG_NODE_W = 120;
const SVG_NODE_H = 50;
const SVG_NODE_GAP_X = 50;
const SVG_RANK_GAP_Y = 100;
const SVG_PAD = 40;
const SVG_NODE_RX = 8;

interface SvgLayoutResult {
  positions: Map<string, { x: number; y: number }>;
  backSet: Set<string>;
  totalW: number;
  totalH: number;
}

export function layoutDiagram(data: DiagramData, opts: DrawioLayoutOpts = {}): SvgLayoutResult {
  const { pageWidth = 800, pageHeight = 600 } = opts;
  const nodeMap = new Map<string, DiagramNode>(data.nodes.map((n) => [n.id, n]));
  const adj = new Map<string, Set<string>>();
  const allNodeIds: string[] = [];
  for (const n of data.nodes) { adj.set(n.id, new Set()); allNodeIds.push(n.id); }
  for (const e of data.edges) { if (nodeMap.has(e.from)) adj.get(e.from)!.add(e.to); }

  const color = new Map<string, number>();
  for (const id of allNodeIds) color.set(id, 0);
  const backSet = new Set<string>();
  function dfs(id: string) {
    color.set(id, 1);
    for (const next of adj.get(id) ?? []) {
      if (color.get(next) === 1) backSet.add(`${id}->${next}`);
      else if (color.get(next) === 0) dfs(next);
    }
    color.set(id, 2);
  }
  for (const id of allNodeIds) if (color.get(id) === 0) dfs(id);

  const rank = new Map<string, number>();
  for (const id of allNodeIds) rank.set(id, 0);
  for (let pass = 0; pass < allNodeIds.length; pass++) {
    for (const e of data.edges) {
      if (backSet.has(`${e.from}->${e.to}`)) continue;
      const proposed = (rank.get(e.from) ?? 0) + 1;
      if (proposed > (rank.get(e.to) ?? 0)) rank.set(e.to, proposed);
    }
  }

  const rankGroups = new Map<number, string[]>();
  for (const id of allNodeIds) {
    const r = rank.get(id)!;
    if (!rankGroups.has(r)) rankGroups.set(r, []);
    rankGroups.get(r)!.push(id);
  }

  const maxRank = Math.max(...Array.from(rank.values()), 0);
  const maxRowLen = Math.max(...Array.from(rankGroups.values()).map((g) => g.length), 1);
  const totalW = Math.max(pageWidth, SVG_PAD * 2 + maxRowLen * (SVG_NODE_W + SVG_NODE_GAP_X) - SVG_NODE_GAP_X);
  const totalH = Math.max(pageHeight, SVG_PAD * 2 + (maxRank + 1) * (SVG_NODE_H + SVG_RANK_GAP_Y) - SVG_RANK_GAP_Y);

  const positions = new Map<string, { x: number; y: number }>();
  for (const [r, ids] of rankGroups) {
    const rowW = ids.length * SVG_NODE_W + (ids.length - 1) * SVG_NODE_GAP_X;
    const startX = (totalW - rowW) / 2;
    for (let i = 0; i < ids.length; i++) {
      positions.set(ids[i]!, { x: startX + i * (SVG_NODE_W + SVG_NODE_GAP_X), y: SVG_PAD + r! * (SVG_NODE_H + SVG_RANK_GAP_Y) });
    }
  }
  return { positions, backSet, totalW, totalH };
}

export function buildDiagramSvgSync(data: DiagramData, opts?: DrawioLayoutOpts): string {
  if (data.nodes.length === 0 && data.edges.length === 0) {
    return `<div class="npp-diagram npp-diagram--empty">(diagram "${escapeHtml(data.name)}": empty)</div>`;
  }

  const { positions, backSet, totalW, totalH } = layoutDiagram(data, opts);
  const uid = `dgm-${data.name}`;

  const parts: string[] = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalW} ${totalH}" width="${totalW}" height="${totalH}" class="npp-diagram-svg" aria-label="diagram ${escapeHtml(data.name)}">`,
    `<defs>`,
    `<marker id="${uid}-arrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">`,
    `<polygon points="0 0, 10 3.5, 0 7" fill="currentColor"/>`,
    `</marker>`,
    `</defs>`,
  ];

  // ── Edges ──
  for (const e of data.edges) {
    const sp = positions.get(e.from)!;
    const tp = positions.get(e.to)!;
    if (!sp || !tp) continue;

    const sCx = sp.x + SVG_NODE_W / 2;
    const sCy = sp.y + SVG_NODE_H / 2;
    const tCx = tp.x + SVG_NODE_W / 2;
    const tCy = tp.y + SVG_NODE_H / 2;

    const isBack = backSet.has(`${e.from}->${e.to}`);
    const isDashed = e.styles["dashed"] === "1";
    const strokeColor = e.styles["color"] ?? (isBack ? "#999" : "currentColor");
    const strokeWidth = parseFloat(e.styles["strokeWidth"] ?? "1.5") || 1.5;

    let sx: number, sy: number, tx: number, ty: number;
    const dy = tCy - sCy;
    if (dy > 0) {
      sx = sCx; sy = sCy + SVG_NODE_H / 2;
      tx = tCx; ty = tCy - SVG_NODE_H / 2;
    } else if (dy < 0) {
      sx = tCx > sCx ? sCx + SVG_NODE_W / 2 : sCx - SVG_NODE_W / 2;
      sy = sCy;
      tx = tCx > sCx ? tCx + SVG_NODE_W / 2 : tCx - SVG_NODE_W / 2;
      ty = tCy;
    } else {
      sx = tCx > sCx ? sCx + SVG_NODE_W / 2 : sCx - SVG_NODE_W / 2;
      sy = sCy;
      tx = tCx > sCx ? tCx - SVG_NODE_W / 2 : tCx + SVG_NODE_W / 2;
      ty = tCy;
    }

    let pathD: string;
    if (isBack) {
      const lx = Math.min(sx, tx) - 40;
      pathD = `M${sx},${sy} C${lx},${sy} ${lx},${ty} ${tx},${ty}`;
    } else if (e.styles["curved"] === "1") {
      const midX = (sx + tx) / 2;
      pathD = `M${sx},${sy} Q${midX},${Math.min(sy, ty) - 20} ${tx},${ty}`;
    } else if (Math.abs(sCx - tCx) < 5) {
      pathD = `M${sx},${sy} L${tx},${ty}`;
    } else {
      const midY = sy + (ty - sy) / 2;
      pathD = `M${sx},${sy} L${sx},${midY} L${tx},${midY} L${tx},${ty}`;
    }

    const dashAttr = isDashed ? ` stroke-dasharray="5,3"` : "";
    parts.push(
      `<path d="${pathD}" fill="none" stroke="${strokeColor}" stroke-width="${strokeWidth}" opacity="${isBack ? "0.6" : "0.85"}"${dashAttr} marker-end="url(#${uid}-arrow)"/>`,
    );

    if (e.label) {
      const lx2 = (sx + tx) / 2;
      const ly2 = (sy + ty) / 2 - 8;
      parts.push(
        `<rect x="${lx2 - 30}" y="${ly2 - 8}" width="60" height="16" fill="white" stroke="#d0d0d0" stroke-width="0.5" rx="2" ry="2"/>`,
        `<text x="${lx2}" y="${ly2 + 4}" text-anchor="middle" style="font-size:11px;font-family:-apple-system,Helvetica,sans-serif;fill:#333">${escapeHtml(e.label)}</text>`,
      );
    }
  }

  // ── Nodes ──
  for (const n of data.nodes) {
    const pos = positions.get(n.id)!;
    if (!pos) continue;

    const x = pos.x;
    const y = pos.y;
    const fill = n.styles["fill"] ?? "#ffffff";
    const strokeCol = n.styles["stroke"] ?? "#888";
    const strokeW = parseFloat(n.styles["strokeWidth"] ?? "1.5") || 1.5;
    const opacity = n.styles["opacity"] ? parseFloat(n.styles["opacity"]) / 100 : 1;
    const fontSize = parseFloat(n.styles["fontSize"] ?? "12");
    const fontCol = n.styles["fontColor"] ?? "currentColor";
    const isRounded = n.styles["round"] === "1";
    const hasShadow = n.styles["shadow"] === "1";

    const attrs = [
      `fill="${fill}" stroke="${strokeCol}" stroke-width="${strokeW}" opacity="${opacity}"`,
      hasShadow ? `style="filter:url(#${uid}-s${n.id.replace(/[^a-zA-Z0-9]/g, '_')})"` : "",
    ].filter(Boolean).join(" ");

    let shadowFilter = "";
    if (hasShadow) {
      const fId = `${uid}-s${n.id.replace(/[^a-zA-Z0-9]/g, '_')}`;
      parts.push(
        `<filter id="${fId}" x="-10%" y="-10%" width="130%" height="130%">`,
        `<feDropShadow dx="2" dy="2" stdDeviation="2" flood-color="#00000030"/>`,
        `</filter>`,
      );
      shadowFilter = ` filter="url(#${fId})"`;
    }

    if (n.shape === "ellipse") {
      parts.push(
        `<ellipse cx="${x + SVG_NODE_W / 2}" cy="${y + SVG_NODE_H / 2}" rx="${SVG_NODE_W / 2}" ry="${SVG_NODE_H / 2}" ${attrs}${shadowFilter}/>`,
      );
    } else if (n.shape === "rhombus" || n.shape === "diamond") {
      const cx = x + SVG_NODE_W / 2;
      const cy = y + SVG_NODE_H / 2;
      parts.push(
        `<polygon points="${cx},${y} ${x + SVG_NODE_W},${cy} ${cx},${y + SVG_NODE_H} ${x},${cy}" ${attrs}${shadowFilter}/>`,
      );
    } else if (isRounded) {
      parts.push(
        `<rect x="${x}" y="${y}" width="${SVG_NODE_W}" height="${SVG_NODE_H}" rx="${SVG_NODE_RX}" ry="${SVG_NODE_RX}" ${attrs}${shadowFilter}/>`
      );
    } else {
      parts.push(
        `<rect x="${x}" y="${y}" width="${SVG_NODE_W}" height="${SVG_NODE_H}" ${attrs}${shadowFilter}/>`
      );
    }

    const textX = x + SVG_NODE_W / 2;
    const textY = y + SVG_NODE_H / 2 + fontSize / 3;
    parts.push(
      `<text x="${textX}" y="${textY}" text-anchor="middle" dominant-baseline="auto" style="font-size:${fontSize}px;font-family:-apple-system,Helvetica,sans-serif;fill:${fontCol}">${escapeHtml(n.label)}</text>`
    );
  }

  parts.push(`</svg>`);
  return `<div class="npp-diagram">${parts.join("")}</div>`;
}
